import { memoize } from "lodash";
import slugify from "slugify";

export interface ProjectInfo {
  owner: string;
  repo: string;
}

export const getGitHubBaseUrl = ({ owner, repo }: ProjectInfo): string =>
  `https://github.com/${owner}/${repo}`;

type CommitUrlOptions = ProjectInfo & { commitHash: string };
export const getCommitUrl = ({
  owner,
  repo,
  commitHash,
}: CommitUrlOptions): string =>
  `${getGitHubBaseUrl({ owner, repo })}/commit/${commitHash}`;

/**
 * Creates a contributing PR to a parent repository
 * Example: https://github.com/reactjs/reactjs.org/compare/main...danilowoz:reactjs.org:sandpack-v1
 */
export const getContributeToParentUrl = ({
  fork,
  forkBranchName,
  parent,
  parentBranchName,
}: Record<
  "fork" | "parent" | "parentBranchName" | "forkBranchName",
  string
>) => {
  if (fork === parent) return false;

  try {
    const parentURL = new URL(parent);
    const forkURL = new URL(fork);

    const getRepoInfo = (url: string) => {
      const [, owner, repo] = url.replace(".git", "").split("/");
      return [owner, repo];
    };

    const [owner, repo] = getRepoInfo(parentURL.pathname);
    const [forkOwner, forkRepo] = getRepoInfo(forkURL.pathname);

    if (!owner || !repo || !forkOwner || !forkRepo) return false;

    return `${getGitHubBaseUrl({
      owner,
      repo,
    })}/compare/${parentBranchName}...${forkOwner}:${forkRepo}:${forkBranchName}`;
  } catch {
    return false;
  }
};

type NewPullRequestUrlOptions = ProjectInfo & {
  sourceBranch: string;
  targetBranch: string;
  commitMessage: string;
};

// GitHub will still fill in the body of PRs from its own first commit truncating behaviour,
// even though we fill in the title using a query. So we are forced to follow
// this rule as we can not create empty body using the query param
const GIT_COMMIT_MAXIMUM_TITLE_LENGTH = 72;

export const getNewPullRequestUrl = ({
  owner,
  repo,
  sourceBranch,
  targetBranch,
  commitMessage,
}: NewPullRequestUrlOptions): string => {
  let prDetails = "";

  if (commitMessage.length > GIT_COMMIT_MAXIMUM_TITLE_LENGTH) {
    const [semantic, description] = commitMessage.split(":");
    const isSematic = semantic && description;

    prDetails = `&title=${
      isSematic ? semantic.trim() + ": " : sourceBranch
    }&body=${encodeURIComponent(description.trim())}`;
  } else {
    prDetails = `&title=${encodeURIComponent(commitMessage)}`;
  }

  return `${getGitHubBaseUrl({
    owner,
    repo,
  })}/compare/${targetBranch}...${sourceBranch}?quick_pull=1${prDetails}`;
};

type PullRequestUrlOptions = ProjectInfo & {
  prNumber: number;
};

export const getPullRequestUrl = ({
  owner,
  repo,
  prNumber,
}: PullRequestUrlOptions): string =>
  `${getGitHubBaseUrl({
    owner,
    repo,
  })}/pull/${prNumber}`;

type BranchUrlOptions = ProjectInfo & { branchName: string };

export const getBranchUrl = ({
  owner,
  repo,
  branchName,
}: BranchUrlOptions): string =>
  `${getGitHubBaseUrl({
    owner,
    repo,
  })}/tree/${branchName}`;

// TODO: export only namespaced to avoid confusion
// with our own paths/urls.
export const githubUrl = {
  branch: getBranchUrl,
  repo: getGitHubBaseUrl,
};

/**
 * @todo: This should probably be passed as an argument to the fn
 */
const MAX_BRANCH_LENGTH = 80;

/**
 * Returns a slugified version of the passed string, to be used as a valid branch name.
 */
export const slugifyBranchName = memoize((name: string): string => {
  if (typeof name !== "string") {
    return "";
  }

  return name
    .substring(0, MAX_BRANCH_LENGTH)
    .split("/")
    .reduce((acc, part, i, parts) => {
      const slugified = slugify(part);

      if (slugified) {
        acc += slugified;
        if (i < parts.length - 1) {
          acc += "/";
        }
      }

      return acc;
    }, "");
});

export const DISCARD_PROMPT_KEY = "CSB_DISCARD_PROMPT_DISMISSED";
